import React, { useState } from 'react';
import {
  NATUREZA_LABELS,
  TIPO_OPERACAO_LABELS,
  FINALIDADE_LABELS,
} from '../constants';
import { CompanyGateway } from '../types';
import {
  StyledCard, CardTitle, StyledFormGroup,
  TooltipWrapper, InfoIcon, DropdownList, DropdownItem,
} from '../cardStyles';

export interface IdentificacaoValues {
  companyEmissorId: string;
  naturezaOperacao: string;
  tipoOperacao: string;
  finalidade: string;
}

interface Props {
  emissoras: CompanyGateway[];
  invoice: any;
  value: IdentificacaoValues;
  onChange: (next: Partial<IdentificacaoValues>) => void;
  readOnly?: boolean;
}

const LabelWithInfo = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

const formatCnpj = (cnpj: string) => {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const isMatriz = (cnpj: string) => cnpj.replace(/\D/g, '').slice(8, 12) === '0001';

// Rótulo da emissora: "Matriz - Cidade/UF" (ou "Filial - …"). O CNPJ vai no subtítulo.
const emissoraLabel = (company: CompanyGateway['Company']) => {
  const city = company.Address?.Neighborhood?.City;
  const cidade = city?.name;
  const uf = city?.State?.uf;
  const local = cidade && uf ? `${cidade}/${uf}` : cidade || uf || 'Local não informado';
  return `${isMatriz(company.cnpj) ? 'Matriz' : 'Filial'} - ${local}`;
};

const IdentificacaoSection: React.FC<Props> = ({
  emissoras,
  invoice,
  value,
  onChange,
  readOnly = false,
}) => {
  const [isEmissoraOpen, setIsEmissoraOpen] = useState(false);
  const [emissoraSearch, setEmissoraSearch] = useState('');

  const selectedEmissora = emissoras.find(
    (cg) => String(cg.companyId) === value.companyEmissorId
  );

  const filteredEmissoras = emissoras.filter((cg) => {
    const s = emissoraSearch.toLowerCase();
    return (
      cg.Company.companyName.toLowerCase().includes(s) ||
      cg.Company.cnpj.replace(/\D/g, '').includes(s.replace(/\D/g, ''))
    );
  });

  return (
    <StyledCard>
      <CardTitle>Identificação</CardTitle>

      <StyledFormGroup style={{ marginBottom: '16px' }}>
        <LabelWithInfo label="Empresa Emissora" tooltip="Empresa responsável pela emissão da NF-e" />
        {readOnly ? (
          <input type="text" readOnly value={invoice?.CompanyEmissor?.companyName || '—'} />
        ) : (
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Selecione a empresa emissora…"
              autoComplete="off"
              value={
                isEmissoraOpen
                  ? emissoraSearch
                  : selectedEmissora
                  ? emissoraLabel(selectedEmissora.Company)
                  : ''
              }
              onChange={(e) => { setEmissoraSearch(e.target.value); setIsEmissoraOpen(true); }}
              onFocus={() => { setEmissoraSearch(''); setIsEmissoraOpen(true); }}
              onBlur={() => setTimeout(() => setIsEmissoraOpen(false), 200)}
              style={{ background: '#fff', textAlign: 'left' }}
            />
            {isEmissoraOpen && filteredEmissoras.length > 0 && (
              <DropdownList style={{ top: 'calc(100% + 4px)', zIndex: 9999, borderRadius: '8px' }}>
                {filteredEmissoras.map((cg) => (
                  <DropdownItem
                    key={cg.companyId}
                    onMouseDown={() => {
                      onChange({ companyEmissorId: String(cg.companyId) });
                      setIsEmissoraOpen(false);
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '12px', color: '#222', fontWeight: 500 }}>
                        {emissoraLabel(cg.Company)}
                      </span>
                      <span style={{ fontSize: '11px', color: '#888' }}>
                        {formatCnpj(cg.Company.cnpj)}
                      </span>
                    </div>
                  </DropdownItem>
                ))}
              </DropdownList>
            )}
          </div>
        )}
      </StyledFormGroup>

      <StyledFormGroup style={{ marginBottom: '16px' }}>
        <LabelWithInfo label="Natureza da Operação" tooltip="Tipo de operação comercial desta NF-e" />
        {readOnly ? (
          <input
            type="text"
            readOnly
            value={NATUREZA_LABELS[invoice?.naturezaOperacao] || invoice?.naturezaOperacao || '—'}
          />
        ) : (
          <select
            value={value.naturezaOperacao}
            onChange={(e) => onChange({ naturezaOperacao: e.target.value })}
            style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', width: '100%' }}
          >
            <option value="">Selecione…</option>
            {Object.entries(NATUREZA_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        )}
      </StyledFormGroup>

      <StyledFormGroup style={{ marginBottom: '16px' }}>
        <LabelWithInfo label="Tipo de Operação" tooltip="Entrada ou saída" />
        {readOnly ? (
          <input
            type="text"
            readOnly
            value={TIPO_OPERACAO_LABELS[invoice?.tipoOperacao] || invoice?.tipoOperacao || '—'}
          />
        ) : (
          <select
            value={value.tipoOperacao}
            onChange={(e) => onChange({ tipoOperacao: e.target.value })}
            style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', width: '100%' }}
          >
            <option value="">Selecione…</option>
            {Object.entries(TIPO_OPERACAO_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        )}
      </StyledFormGroup>

      <StyledFormGroup>
        <LabelWithInfo label="Finalidade" tooltip="Finalidade da NF-e" />
        {readOnly ? (
          <input
            type="text"
            readOnly
            value={FINALIDADE_LABELS[invoice?.finalidade] || invoice?.finalidade || '—'}
          />
        ) : (
          <select
            value={value.finalidade}
            onChange={(e) => onChange({ finalidade: e.target.value })}
            style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', width: '100%' }}
          >
            <option value="">Selecione…</option>
            {Object.entries(FINALIDADE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        )}
      </StyledFormGroup>
    </StyledCard>
  );
};

export default IdentificacaoSection;
